import { FileText, Image } from 'lucide-react'
import { Badge } from '../Badge'
import type { LibraryDocument, DocumentType } from '@/api/client'

const DOC_TYPE_LABELS: Record<DocumentType, string> = {
  Receipt: 'Receipt',
  ProofOfPayment: 'Proof of Payment',
  LetterOfMedicalNecessity: 'LMN',
  ExplanationOfBenefits: 'EOB',
  Other: 'Other',
}

const DOC_TYPE_VARIANT: Record<DocumentType, 'navy' | 'gold' | 'green' | 'gray'> = {
  Receipt: 'navy',
  ProofOfPayment: 'green',
  LetterOfMedicalNecessity: 'gold',
  ExplanationOfBenefits: 'gray',
  Other: 'gray',
}

interface DocumentCardProps {
  document: LibraryDocument
  previewUrl?: string
  onClick: () => void
}

export function DocumentCard({ document, previewUrl, onClick }: DocumentCardProps) {
  const isImage = document.contentType.startsWith('image/')

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div
      onClick={onClick}
      className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden cursor-pointer hover:shadow-md hover:border-[#1B365D]/30 transition-all group"
    >
      {/* Thumbnail area */}
      <div className="h-32 bg-[#F7FAFC] flex items-center justify-center overflow-hidden">
        {isImage && previewUrl ? (
          <img
            src={previewUrl}
            alt={document.fileName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            {isImage ? (
              <Image size={32} className="text-[#A0AEC0]" />
            ) : (
              <FileText size={32} className="text-[#A0AEC0]" />
            )}
            <span className="text-[11px] text-[#A0AEC0] uppercase">
              {document.contentType.split('/')[1] || 'file'}
            </span>
          </div>
        )}
      </div>

      {/* Info area */}
      <div className="p-3">
        <div className="flex items-start gap-2 mb-1">
          <span className="text-[13px] font-medium text-[#1B365D] truncate flex-1">
            {document.fileName}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Badge
            variant={DOC_TYPE_VARIANT[document.documentType] || 'gray'}
            size="category"
          >
            {DOC_TYPE_LABELS[document.documentType] || document.documentType}
          </Badge>
          <span className="text-[11px] text-[#A0AEC0]">
            {formatSize(document.fileSize)}
          </span>
        </div>

        <div className="text-[11px] text-[#A0AEC0] mb-2">
          {formatDate(document.createdAt)}
        </div>

        {/* Linked expenses */}
        {document.linkedExpenses.length > 0 ? (
          <div className="space-y-1">
            {document.linkedExpenses.slice(0, 2).map((expense) => (
              <div
                key={expense.id}
                className="text-[11px] bg-[#1B365D]/5 text-[#1B365D] rounded px-2 py-1 truncate"
              >
                {expense.provider} &middot; {formatCurrency(expense.amount)}
              </div>
            ))}
            {document.linkedExpenses.length > 2 && (
              <div className="text-[11px] text-[#A0AEC0]">
                +{document.linkedExpenses.length - 2} more
              </div>
            )}
          </div>
        ) : (
          <div className="text-[11px] bg-[#E2E8F0]/50 text-[#A0AEC0] rounded px-2 py-1 text-center">
            Unlinked
          </div>
        )}
      </div>
    </div>
  )
}
